/* eslint-disable react-hooks/exhaustive-deps */
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { buildGroupedOptions } from "../../utils/buildGroupedOptions";
import { selectStyles } from "../../utils/selectStyel";

// Main Modal
export const FieldEditorModal = ({
  isOpen,
  onClose,
  fieldData,
  onSave,
  mainSchemaData,
  sectionKey,
}) => {
  const { register, handleSubmit, reset, control, watch } = useForm({
    defaultValues: {
      MESSAGE: "",
      TOOLTIP: "",
      CDC_ID: "",
      TYPE: "String",
      EDITABLE: true,
      VISIBLE: true,
      REFERENCE_CODE: "",
      REFERENCE_SUBGROUP: "",
      REFERENCE_TIMESTAMP: "",
      CONDITION: {},
      PREVIEW_VALUE: [],
    },
  });

  const type = watch("TYPE");
  const [subgroupOptions, setSubgroupOptions] = useState([]);
  const [editableKey, setEditableKey] = useState("");
  const [editableNestedKey, setEditableNestedKey] = useState("");
  useEffect(() => {
    async function fetchConfig() {
      try {
        const res = await fetch("/api/listConfig?type=CODE");
        const data = await res.json();
        const options = data.map((item) => ({
          value: item.id,
          label: item.DESCRIPTION || item.id,
        }));
        setSubgroupOptions(options);
      } catch (err) {
        console.error("Failed to fetch subgroup options", err);
      }
    }
    fetchConfig();
  }, []);

  // Load fieldData
  useEffect(() => {
    if (!fieldData) return;
    setEditableKey(fieldData?.fieldKey || "");
    setEditableNestedKey(fieldData?.nestedKey || "");
    const data = { ...fieldData };
    if (fieldData?.REFERENCE) {
      data.REFERENCE_TIMESTAMP = fieldData.REFERENCE.TIMESTAMP || "";
      data.REFERENCE_CODE = fieldData.REFERENCE.CODE || "";
      data.REFERENCE_SUBGROUP = fieldData.REFERENCE.SUBGROUP || "";
    }
    if (fieldData?.CONDITION) {
      data.CONDITION = fieldData.CONDITION;
    }
    reset(data);
  }, [fieldData, reset]);

  const handleSave = (data) => {
    if (data.TYPE === "Dropdown" || data.TYPE === "Multiselect") {
      data.REFERENCE = {
        TIMESTAMP: data.REFERENCE_TIMESTAMP,
        CODE: data.REFERENCE_CODE,
        SUBGROUP: data.REFERENCE_SUBGROUP || undefined,
      };
    }

    delete data.REFERENCE_TIMESTAMP;
    delete data.REFERENCE_CODE;
    delete data.REFERENCE_SUBGROUP;

    onSave({
      ...data,
      __NEW_FIELD_KEY__: editableKey, // top-level fieldKey
      __NEW_NESTED_KEY__: editableNestedKey, // nestedKey (if any)
    });
  };

  const options = Object.entries(
    mainSchemaData?.CONFIG?.[sectionKey]?.FIELDS || {},
  )
    .filter(([_, arr]) => Array.isArray(arr))
    .flatMap(([fieldKey, arr]) =>
      arr.flatMap((item) =>
        Object.entries(item)
          .filter(([key]) => key !== "PREVIEW")
          .map(([key, def]) => ({
            value: key,
            label: def?.MESSAGE || key,
            type: def?.TYPE || "Unknown",
          })),
      ),
    );

  return (
    <Modal
      isOpen={isOpen}
      size="6xl"
      onClose={onClose}
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="xl">{fieldData?.fieldKey}</Text>
          <Text fontSize="md"> {fieldData?.nestedKey}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody maxHeight="78vh" overflowY="auto">
          <SimpleGrid columns={2} spacing={5} w="100%">
            {type === "Preview" && (
              <Box>
                <FormControl>
                  <FormLabel>Message</FormLabel>
                  <Input
                    {...register("MESSAGE")}
                    borderRadius="md"
                    borderColor="gray.300"
                    bg="white"
                  />
                </FormControl>

                <Controller
                  control={control}
                  name="PREVIEW_VALUE"
                  render={({ field }) => {
                    const selectedOptions = Array.isArray(field.value)
                      ? options.filter((opt) => field.value.includes(opt.value))
                      : options.find((opt) => opt.value === field.value) ||
                        null;

                    return (
                      <FormControl>
                        <FormLabel>PREVIEW VALUE</FormLabel>
                        <ReactSelect
                          {...field}
                          options={options}
                          placeholder="Select Field"
                          value={selectedOptions}
                          onChange={(selected) => {
                            if (Array.isArray(selected)) {
                              field.onChange(selected.map((opt) => opt.value));
                            } else {
                              field.onChange(selected?.value || "");
                            }
                          }}
                          isMulti
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={selectStyles}
                        />
                      </FormControl>
                    );
                  }}
                />
              </Box>
            )}
            {type !== "Preview" && (
              <Box>
                <FormControl mb={3}>
                  <FormLabel>
                    Field Key{" "}
                    {editableNestedKey ? "(Nested Key)" : "(Top-level Key)"}
                  </FormLabel>
                  <Input
                    value={editableNestedKey || editableKey} // show nestedKey if present
                    onChange={(e) => {
                      const formatted = e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9_]/g, "_");
                      if (editableNestedKey) {
                        setEditableNestedKey(formatted);
                      } else {
                        setEditableKey(formatted);
                      }
                    }}
                    placeholder="FIELD_KEY"
                    borderRadius="md"
                    borderColor="gray.300"
                    bg="white"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Message</FormLabel>
                  <Input
                    {...register("MESSAGE")}
                    borderRadius="md"
                    borderColor="gray.300"
                    bg="white"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Tooltip</FormLabel>
                  <Input
                    {...register("TOOLTIP")}
                    borderRadius="md"
                    borderColor="gray.300"
                    bg="white"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>CDC ID</FormLabel>
                  <Input
                    {...register("CDC_ID")}
                    borderRadius="md"
                    borderColor="gray.300"
                    bg="white"
                  />
                </FormControl>
                <Flex align="center" gap={5} mt={2}>
                  <FormControl display="flex">
                    <FormLabel>Editable</FormLabel>
                    <Controller
                      control={control}
                      name="EDITABLE"
                      render={({ field }) => (
                        <Switch
                          isChecked={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </FormControl>
                  <FormControl display="flex">
                    <FormLabel>Visible</FormLabel>
                    <Controller
                      control={control}
                      name="VISIBLE"
                      render={({ field }) => (
                        <Switch
                          isChecked={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </FormControl>
                </Flex>
                <FormControl>
                  <FormLabel>Type</FormLabel>
                  <Select
                    {...register("TYPE")}
                    borderRadius="md"
                    borderColor="gray.300"
                    bg="white"
                  >
                    <option value="String">String</option>
                    <option value="Dropdown">Dropdown</option>
                    <option value="Number">Number</option>
                    <option value="Date">Date</option>
                    <option value="Multiselect">Multiselect</option>
                    <option value="TextBox">TextBox</option>
                  </Select>
                </FormControl>
                {type === "Multiselect" && (
                  <FormControl my={2}>
                    <FormLabel>Multiselect Type</FormLabel>
                    <Controller
                      control={control}
                      name="MULTISELECT_TYPE"
                      render={({ field }) => (
                        <RadioGroup {...field} size="lg">
                          <VStack spacing={2} alignItems="start">
                            <Radio value="Select_All_With_CheckBox">
                              Select All w/ Checkbox
                            </Radio>
                            <Radio value="Checkbox" fontWeight={500}>
                              Checkbox
                            </Radio>
                            <Radio value="Radio">Radio</Radio>
                          </VStack>
                        </RadioGroup>
                      )}
                    />
                  </FormControl>
                )}

                {(type === "Dropdown" || type === "Multiselect") && (
                  <>
                    <FormControl>
                      <FormLabel>Reference Code</FormLabel>
                      <Controller
                        control={control}
                        name="REFERENCE_CODE"
                        render={({ field }) => (
                          <ReactSelect
                            {...field}
                            options={subgroupOptions}
                            value={
                              subgroupOptions.find(
                                (opt) => opt.value === field.value,
                              ) || null
                            }
                            onChange={(selected) =>
                              field.onChange(selected?.value || "")
                            }
                            placeholder="Select Code..."
                            isClearable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={selectStyles}
                          />
                        )}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Reference Subgroup</FormLabel>
                      <Input
                        {...register("REFERENCE_SUBGROUP")}
                        borderRadius="md"
                        borderColor="gray.300"
                        bg="white"
                      />
                    </FormControl>
                  </>
                )}
              </Box>
            )}

            {/* Sentence-style Condition Editor */}
            {type !== "Preview" && (
              <Box>
                <FormLabel>Conditions</FormLabel>
                <ConditionGroup
                  control={control}
                  register={register}
                  path="CONDITION"
                  groupData={fieldData?.CONDITION}
                  fieldKey={fieldData?.fieldKey}
                  mainSchemaData={mainSchemaData}
                  sectionKey={sectionKey}
                />
              </Box>
            )}
          </SimpleGrid>
        </ModalBody>

        <ModalFooter>
          <Button mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit(handleSave)}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Recursive ConditionGroup component

export const ConditionGroup = ({
  control,
  register,
  path,
  groupData,
  fieldKey,
  sectionKey,
  mainSchemaData,
}) => {
  const group = groupData || { Operator: "all", Order: "high", Value: [] };
  if (!group.Field && fieldKey) {
    group.Field = `${fieldKey}_CONDITION`;
  }
  if (!group.Operator) group.Operator = "all";
  if (!group.Order) group.Order = "high";
  if (!group.Value) group.Value = [];

  const { fields, append, remove } = useFieldArray({
    control,
    name: `${path}.Value`,
  });

  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current && fields.length === 0) {
      if (!groupData) {
        append({ Field: "", Operator: "==", Value: "", Order: "base" });
      } else if (Array.isArray(group.Value) && group.Value.length > 0) {
        group.Value.forEach((item) => append(item));
      } else if (group.Order === "high") {
        append({ Field: "", Operator: "==", Value: "", Order: "base" });
      }
      initialized.current = true;
    }
  }, [groupData, group.Order, append, fields.length, group.Value]);

  // Form paths
  const fieldField = `${path}.Field`;
  const operatorField = `${path}.Operator`;
  const orderField = `${path}.Order`;

  const groupedOptions = buildGroupedOptions(mainSchemaData.CONFIG);
  return (
    <VStack
      spacing={3}
      align="stretch"
      border="1px solid #CBD5E0"
      p={3}
      borderRadius="md"
      bg="gray.50"
      ml={path.split(".").length > 1 ? 6 : 0}
    >
      <input type="hidden" {...register(fieldField)} value={group.Field} />
      <input type="hidden" {...register(orderField)} value={group.Order} />

      {group.Order === "high" && (
        <Controller
          control={control}
          name={operatorField}
          defaultValue={group.Operator}
          render={({ field }) => (
            <Select {...field} w="150px">
              <option value="all">All</option>
              <option value="any">Any</option>
            </Select>
          )}
        />
      )}

      {group.Order === "high" &&
        fields.map((item, index) => {
          const fieldPath = `${path}.Value.${index}`;

          if (item.Order === "high" || Array.isArray(item.Value)) {
            return (
              <Box key={index} position="relative">
                <ConditionGroup
                  control={control}
                  register={register}
                  path={fieldPath}
                  groupData={item}
                  fieldKey={fieldKey}
                  mainSchemaData={mainSchemaData}
                  sectionKey={sectionKey}
                />
                <IconButton
                  icon={<DeleteIcon />}
                  onClick={() => remove(index)}
                  size="xs"
                  colorScheme="red"
                  aria-label="Remove Group"
                  position="absolute"
                  right={0}
                  top={0}
                />
              </Box>
            );
          }

          // Base condition
          return (
            <Flex key={index} align="center" gap={3}>
              <input
                type="hidden"
                {...register(`${fieldPath}.Order`)}
                value="base"
              />

              <Box w="300px">
                <Controller
                  control={control}
                  name={`${fieldPath}.Field`}
                  defaultValue={item.Field || ""} // raw string value
                  render={({ field }) => {
                    // flatten all options from groupedOptions
                    const flatOptions = groupedOptions.flatMap(
                      (group) => group.options || [],
                    );

                    return (
                      <ReactSelect
                        options={groupedOptions}
                        placeholder="Select Field"
                        value={
                          flatOptions.find(
                            (opt) => opt.value === field.value,
                          ) || null
                        }
                        onChange={(selected) => field.onChange(selected?.value)}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={selectStyles}
                      />
                    );
                  }}
                />
              </Box>

              <Select
                size="sm"
                {...register(`${fieldPath}.Operator`)}
                defaultValue={item.Operator}
                w="130px"
              >
                <option value="==">is equal to</option>
                <option value="!=">is not equal to</option>
                <option value=">">is greater than</option>
                <option value="<">is less than</option>
                <option value=">=">is greater or equal</option>
                <option value="<=">is less or equal</option>
                <option value="in">is in</option>
                <option value="contains">contains</option>
              </Select>
              <Input
                size="sm"
                {...register(`${fieldPath}.Value`)}
                defaultValue={item.Value}
                w="100px"
                placeholder="Value"
              />
              <IconButton
                icon={<DeleteIcon />}
                onClick={() => remove(index)}
                size="xs"
                colorScheme="red"
                aria-label="Remove Condition"
              />
            </Flex>
          );
        })}
      {/* Add new condition / group buttons */}
      {group.Order === "high" && (
        <Flex gap={2} mt={2}>
          <Button
            size="sm"
            leftIcon={<AddIcon />}
            onClick={() =>
              append({ Field: "", Operator: "==", Value: "", Order: "base" })
            }
          >
            Add Condition
          </Button>
          <Button
            size="sm"
            leftIcon={<AddIcon />}
            onClick={() =>
              append({
                Field: "",
                Operator: "",
                Value: [],
                Order: "high",
              })
            }
          >
            Add Group
          </Button>
        </Flex>
      )}
    </VStack>
  );
};
